import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import {
    getColdCandidateResume,
    getVacancyById,
    inviteColdCandidate,
    saveColdCandidate,
    searchColdCandidates,
    getColdSearchSettings,
    updateColdSearchSettings,
} from "../services/vacancyApi";
import CandidateProfileCard from "../components/candidates/CandidateProfileCard";

function salaryText(salary) {
    if (!salary || (salary.from == null && salary.to == null)) return "Зарплата не указана";
    const currency = salary.currency || "RUR";
    const amount = salary.from != null && salary.to != null
        ? `${salary.from.toLocaleString("ru-RU")}–${salary.to.toLocaleString("ru-RU")}`
        : salary.from != null
            ? `от ${salary.from.toLocaleString("ru-RU")}`
            : `до ${salary.to.toLocaleString("ru-RU")}`;
    return `${amount} ${currency === "RUR" ? "₽" : currency}`;
}

function coldSearchErrorText(reason, fallback = "Не удалось получить резюме HH") {
    const message = String(reason?.message || "").trim();
    if (reason?.status === 404 || /^not found$/i.test(message)) {
        return "Холодный подбор для этой вакансии сейчас недоступен. Проверьте, что вакансия опубликована на HH.ru, и повторите попытку.";
    }
    return message || fallback;
}

function experienceText(months) {
    if (months == null) return "Опыт не указан";
    if (months < 12) return `${months} мес.`;
    const years = Math.floor(months / 12);
    const rest = months % 12;
    return rest ? `${years} г. ${rest} мес.` : `${years} г.`;
}

function initials(name, title) {
    const source = String(name || title || "К").trim();
    return source.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

export function coldResumeToCandidate(resume, fallback = {}) {
    const experienceMonths = resume?.experience_months ?? fallback?.experience_months;
    const salaryAmount = resume?.salary_amount ?? resume?.salary?.amount;
    const salaryCurrency = resume?.salary_currency || resume?.salary?.currency || "RUR";
    return {
        ...fallback,
        ...resume,
        full_name: resume?.full_name || resume?.name || fallback?.name || fallback?.title || "Кандидат",
        hard_skills: resume?.hard_skills || resume?.skill_set || [],
        experience_label: experienceText(experienceMonths),
        salary_label: salaryAmount != null
            ? `${Number(salaryAmount).toLocaleString("ru-RU")} ${salaryCurrency === "RUR" ? "₽" : salaryCurrency}`
            : salaryText(resume?.salary || fallback?.salary),
        hh_resume_link: resume?.alternate_url || fallback?.alternate_url,
        resume_update_date: resume?.updated_at || fallback?.updated_at,
    };
}

const FILTERS = [
    ["all", "Все"],
    ["new", "Новые"],
    ["saved", "Сохранённые"],
    ["invited", "Приглашённые"],
];

export default function ColdSearchPage() {
    const { vacancyId } = useParams();
    const [vacancy, setVacancy] = useState(null);
    const [items, setItems] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [shortage, setShortage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState("");
    const [inviteError, setInviteError] = useState("");
    const [savingId, setSavingId] = useState(null);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [sort, setSort] = useState("relevance");
    const [resumeModal, setResumeModal] = useState(null);
    const [coldSearchSettings, setColdSearchSettings] = useState(null);
    const [limitForm, setLimitForm] = useState({ vacancy: "10", daily: "100", monthly: "2000" });
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsError, setSettingsError] = useState("");

    const load = useCallback(async (page = 0) => {
        setLoading(true);
        setError("");
        try {
            const result = await searchColdCandidates(vacancyId, page);
            setItems((previous) => page === 0
                ? (result.items || [])
                : [...previous, ...(result.items || []).filter(
                    (item) => !previous.some((existing) => existing.id === item.id)
                )]);
            setNextPage(result.next_page);
            setShortage(result.shortage || 0);
            if (result.limits) setColdSearchSettings(result.limits);
        } catch (reason) {
            setError(coldSearchErrorText(reason));
        } finally {
            setLoading(false);
        }
    }, [vacancyId]);

    useEffect(() => {
        let active = true;
        getVacancyById(vacancyId)
            .then((data) => { if (active) setVacancy(data); })
            .catch((reason) => { if (active) setError(coldSearchErrorText(reason, "Вакансия не найдена")); });
        load(0);
        return () => { active = false; };
    }, [vacancyId, load]);

    const loadLimitSettings = useCallback(async () => {
        setSettingsLoading(true);
        setSettingsError("");
        try {
            const settings = await getColdSearchSettings(vacancyId);
            setColdSearchSettings(settings);
            setLimitForm({
                vacancy: String(settings?.vacancy_limit ?? 10),
                daily: String(settings?.daily_limit ?? 100),
                monthly: String(settings?.monthly_limit ?? 2000),
            });
        } catch (reason) {
            setSettingsError(reason?.message || "Не удалось загрузить настройки холодного поиска");
        } finally {
            setSettingsLoading(false);
        }
    }, [vacancyId]);

    useEffect(() => {
        loadLimitSettings();
    }, [loadLimitSettings]);

    const saveLimitSettings = async (event) => {
        event.preventDefault();
        const vacancyLimit = Number(limitForm.vacancy);
        const dailyLimit = Number(limitForm.daily);
        const monthlyLimit = Number(limitForm.monthly);
        if (![vacancyLimit, dailyLimit, monthlyLimit].every(Number.isInteger)
            || vacancyLimit < 1 || vacancyLimit > 100
            || dailyLimit < 1 || dailyLimit > 10000
            || monthlyLimit < dailyLimit || monthlyLimit > 100000) {
            setSettingsError("Проверьте лимиты: на вакансию — 1–100, в день — 1–10 000, в месяц — не меньше дневного и не больше 100 000.");
            return;
        }
        setSettingsSaving(true);
        setSettingsError("");
        try {
            const settings = await updateColdSearchSettings(vacancyId, {
                vacancy_limit: vacancyLimit,
                daily_limit: dailyLimit,
                monthly_limit: monthlyLimit,
            });
            setColdSearchSettings(settings);
            setNotice("Лимиты холодного подбора сохранены.");
            await loadLimitSettings();
        } catch (reason) {
            setSettingsError(reason?.message || "Не удалось сохранить лимиты холодного поиска");
        } finally {
            setSettingsSaving(false);
        }
    };

    const openInvite = (item) => {
        setSelected(item);
        setMessage(`Здравствуйте! Ваш опыт по резюме «${item.title}» может подойти для вакансии «${vacancy?.name || "в нашей компании"}». Приглашаем вас на собеседование. Если вам интересно предложение, ответьте на это приглашение.`);
        setNotice("");
        setInviteError("");
    };

    const submitInvite = async (event) => {
        event.preventDefault();
        if (!selected || sending || message.trim().length < 10) return;
        setSending(true);
        try {
            const result = await inviteColdCandidate(vacancyId, selected.id, message.trim());
            setItems((previous) => previous.map((item) => item.id === selected.id
                ? { ...item, invited: true, candidate_id: result.candidate_id ?? item.candidate_id }
                : item));
            setSelected(null);
            setNotice(result.warning || "Приглашение отправлено в HH.ru. Кандидат доступен в разделе «Кандидаты».");
        } catch (reason) {
            setInviteError(reason?.message || "Не удалось отправить приглашение");
        } finally {
            setSending(false);
        }
    };

    const saveCandidate = async (item) => {
        if (savingId) return;
        setSavingId(item.id);
        setError("");
        try {
            const result = await saveColdCandidate(vacancyId, item.id);
            setItems((previous) => previous.map((candidate) => candidate.id === item.id
                ? { ...candidate, candidate_id: result.candidate_id }
                : candidate));
            setNotice("Кандидат сохранён со статусом «Холодный контакт».");
        } catch (reason) {
            setError(reason?.message || "Не удалось сохранить кандидата");
        } finally {
            setSavingId(null);
        }
    };

    const openResume = async (item) => {
        setResumeModal({ item, loading: true, candidate: null, error: "" });
        try {
            const resume = await getColdCandidateResume(vacancyId, item.id);
            setResumeModal((current) => current?.item?.id === item.id
                ? { ...current, loading: false, candidate: coldResumeToCandidate(resume, item) }
                : current);
        } catch (reason) {
            setResumeModal((current) => current?.item?.id === item.id
                ? { ...current, loading: false, error: coldSearchErrorText(reason, "Не удалось открыть резюме") }
                : current);
        }
    };

    const counts = useMemo(() => ({
        all: items.length,
        new: items.filter((item) => !item.candidate_id && !item.invited).length,
        saved: items.filter((item) => Boolean(item.candidate_id) && !item.invited).length,
        invited: items.filter((item) => Boolean(item.invited)).length,
    }), [items]);

    const visibleItems = useMemo(() => {
        const normalized = query.trim().toLocaleLowerCase("ru-RU");
        const filtered = items.filter((item) => {
            const matchesFilter = filter === "all"
                || (filter === "new" && !item.candidate_id && !item.invited)
                || (filter === "saved" && item.candidate_id && !item.invited)
                || (filter === "invited" && item.invited);
            if (!matchesFilter) return false;
            if (!normalized) return true;
            return [item.title, item.name, item.area]
                .some((value) => String(value || "").toLocaleLowerCase("ru-RU").includes(normalized));
        });
        return [...filtered].sort((left, right) => {
            if (sort === "experience") return (right.experience_months || 0) - (left.experience_months || 0);
            if (sort === "salary") {
                const salaryValue = (item) => item.salary?.from ?? item.salary?.to ?? 0;
                return salaryValue(right) - salaryValue(left);
            }
            return 0;
        });
    }, [filter, items, query, sort]);

    return (
        <MainLayout className="p-4 sm:p-6 lg:p-8">
            <div className="mx-auto max-w-[1440px] space-y-5">
                <section className="relative overflow-hidden rounded-[28px] bg-slate-950 px-5 py-6 text-white shadow-xl shadow-slate-200/70 sm:px-8 sm:py-8">
                    <div className="absolute -right-24 -top-28 h-72 w-72 rounded-full bg-amber-400/20 blur-3xl" />
                    <div className="absolute -bottom-32 left-1/3 h-64 w-64 rounded-full bg-sky-500/10 blur-3xl" />
                    <div className="relative">
                        <div className="flex flex-wrap items-start justify-between gap-5">
                            <div className="min-w-0">
                                <Link to="/vacancies" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition hover:text-white">
                                    <span aria-hidden="true">←</span> К списку вакансий
                                </Link>
                                <div className="mt-5 flex items-center gap-3">
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-400 text-2xl text-slate-950 shadow-lg shadow-amber-950/20">⌕</span>
                                    <div className="min-w-0">
                                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-300">Поиск в базе HH.ru</p>
                                        <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Холодный подбор</h1>
                                    </div>
                                </div>
                                <p className="mt-4 max-w-2xl text-base text-slate-300">
                                    Кандидаты для вакансии <span className="font-semibold text-white">{vacancy?.name || `#${vacancyId}`}</span>, которые ещё не откликались на неё.
                                </p>
                            </div>
                            <button type="button" onClick={() => load(0)} disabled={loading}
                                className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20 disabled:cursor-wait disabled:opacity-60">
                                <svg className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 4v5h5M4 13a8.1 8.1 0 0 0 15.5 2M20 20v-5h-5" /></svg>
                                {loading ? "Обновляем…" : "Обновить подбор"}
                            </button>
                        </div>

                        <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {[
                                ["Найдено", counts.all, "text-white"],
                                ["Новые", counts.new, "text-sky-300"],
                                ["Сохранено", counts.saved, "text-amber-300"],
                                ["Приглашено", counts.invited, "text-emerald-300"],
                            ].map(([label, value, tone]) => (
                                <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.07] px-4 py-3 backdrop-blur-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                                    <p className={`mt-1 text-2xl font-bold ${tone}`}>{value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <details className="group rounded-2xl border border-amber-200/80 bg-amber-50/70 px-5 py-4 text-sm text-amber-950">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold">
                        <span className="flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-200/70">i</span> Как работает холодный подбор</span>
                        <span className="text-amber-700 transition group-open:rotate-180">⌄</span>
                    </summary>
                    <p className="mt-3 max-w-4xl border-t border-amber-200/70 pt-3 leading-6 text-amber-900/80">
                        Первая найденная пачка сохраняется в HR-платформе и при повторном открытии загружается без нового запроса к HH.ru. Кнопка «Показать ещё» получает и сохраняет только следующую пачку. Полное резюме также запрашивается у HH.ru лишь при первом открытии.
                    </p>
                </details>

                <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6" aria-labelledby="cold-search-limits-title">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 id="cold-search-limits-title" className="text-lg font-bold text-slate-900">Лимиты холодного поиска</h2>
                                {!settingsLoading && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">Лимиты действуют</span>}
                            </div>
                            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
                                Лимит вакансии ограничивает новые карточки по конкретной вакансии, общие лимиты — суммарную загрузку кандидатов из HH.ru.
                            </p>
                        </div>
                        {coldSearchSettings && (
                            <div className="rounded-xl bg-slate-50 px-4 py-2 text-xs text-slate-600">
                                Сегодня загружено <b className="text-slate-900">{coldSearchSettings.used_today}</b> из {coldSearchSettings.daily_limit} · за месяц <b className="text-slate-900">{coldSearchSettings.used_this_month}</b> из {coldSearchSettings.monthly_limit}
                            </div>
                        )}
                    </div>

                    {settingsLoading ? (
                        <p className="mt-5 text-sm text-slate-500">Загружаем настройки…</p>
                    ) : (
                        <form onSubmit={saveLimitSettings} className="mt-5">
                            <div className="grid gap-4 md:grid-cols-3">
                                <label className="block">
                                    <span className="text-sm font-semibold text-slate-800">Лимит на вакансию</span>
                                    <input aria-label="Лимит на вакансию" type="number" min="1" max="100" value={limitForm.vacancy}
                                        onChange={(event) => setLimitForm((current) => ({ ...current, vacancy: event.target.value }))}
                                        className="mt-2 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:bg-white focus:ring-2 focus:ring-amber-100" />
                                    <span className="mt-1 block text-xs text-slate-400">
                                        Для «{vacancy?.name || vacancyId}» уже загружено {coldSearchSettings?.used_for_vacancy ?? 0}
                                    </span>
                                </label>
                                <label className="block">
                                    <span className="text-sm font-semibold text-slate-800">Общий лимит в день</span>
                                    <input aria-label="Общий лимит в день" type="number" min="1" max="10000" value={limitForm.daily}
                                        onChange={(event) => setLimitForm((current) => ({ ...current, daily: event.target.value }))}
                                        className="mt-2 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:bg-white focus:ring-2 focus:ring-amber-100" />
                                    <span className="mt-1 block text-xs text-slate-400">Суммарно по всем активным вакансиям</span>
                                </label>
                                <label className="block">
                                    <span className="text-sm font-semibold text-slate-800">Общий лимит в месяц</span>
                                    <input aria-label="Общий лимит в месяц" type="number" min="1" max="100000" value={limitForm.monthly}
                                        onChange={(event) => setLimitForm((current) => ({ ...current, monthly: event.target.value }))}
                                        className="mt-2 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:bg-white focus:ring-2 focus:ring-amber-100" />
                                    <span className="mt-1 block text-xs text-slate-400">Не может быть меньше дневного лимита</span>
                                </label>
                            </div>
                            {settingsError && <p role="alert" className="mt-3 text-sm font-medium text-rose-700">{settingsError}</p>}
                            <div className="mt-4 flex justify-end">
                                <button type="submit" disabled={settingsSaving}
                                    className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-wait disabled:opacity-60">
                                    {settingsSaving ? "Сохраняем…" : "Сохранить лимиты"}
                                </button>
                            </div>
                        </form>
                    )}
                </section>

                {error ? (
                    <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-900 shadow-sm">
                        <div className="flex min-w-0 items-start gap-3">
                            <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-lg font-bold text-rose-600 shadow-sm">!</span>
                            <div><p className="font-bold">Не удалось загрузить подбор</p><p className="mt-1 text-sm leading-5 text-rose-800">{error}</p></div>
                        </div>
                        <button type="button" onClick={() => load(0)} disabled={loading} className="rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-800 transition hover:bg-rose-100 disabled:opacity-50">Попробовать снова</button>
                    </div>
                ) : null}
                {notice ? (
                    <div role="status" className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-900 shadow-sm">
                        <span className="flex items-center gap-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white">✓</span>{notice}</span>
                        <button type="button" onClick={() => setNotice("")} aria-label="Закрыть уведомление" className="text-lg text-emerald-700 hover:text-emerald-950">×</button>
                    </div>
                ) : null}

                {items.length > 0 ? (
                    <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                            <div className="flex flex-wrap gap-2" role="group" aria-label="Фильтр кандидатов">
                                {FILTERS.map(([key, label]) => (
                                    <button key={key} type="button" onClick={() => setFilter(key)}
                                        className={`rounded-xl px-3.5 py-2 text-sm font-semibold transition ${filter === key ? "bg-slate-900 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                                        {label} <span className={`ml-1 ${filter === key ? "text-slate-300" : "text-slate-400"}`}>{counts[key]}</span>
                                    </button>
                                ))}
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row">
                                <label className="relative block min-w-[260px]">
                                    <span className="sr-only">Поиск по кандидатам</span>
                                    <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></svg>
                                    <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Имя, должность или город"
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-amber-400 focus:bg-white focus:ring-2 focus:ring-amber-100" />
                                </label>
                                <label>
                                    <span className="sr-only">Сортировка кандидатов</span>
                                    <select value={sort} onChange={(event) => setSort(event.target.value)} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-amber-400 sm:w-auto">
                                        <option value="relevance">По релевантности</option>
                                        <option value="experience">Сначала с опытом</option>
                                        <option value="salary">По зарплате</option>
                                    </select>
                                </label>
                            </div>
                        </div>
                    </section>
                ) : null}

                {loading && items.length === 0 ? (
                    <div aria-label="Ищем подходящие резюме" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {[0, 1, 2].map((key) => <div key={key} className="h-80 animate-pulse rounded-[24px] border border-slate-200 bg-white p-5"><div className="h-12 w-12 rounded-2xl bg-slate-200" /><div className="mt-5 h-5 w-2/3 rounded bg-slate-200" /><div className="mt-3 h-4 w-1/2 rounded bg-slate-100" /><div className="mt-8 h-20 rounded-2xl bg-slate-100" /></div>)}
                    </div>
                ) : null}

                {!loading && !error && items.length === 0 ? (
                    <div className="rounded-[24px] border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-sm">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-3xl">⌕</div>
                        <h2 className="mt-5 text-xl font-bold text-slate-900">Подходящих резюме пока нет</h2>
                        <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">Попробуйте обновить подбор позже или скорректировать требования вакансии на HH.ru.</p>
                    </div>
                ) : null}

                {items.length > 0 && visibleItems.length === 0 ? (
                    <div className="rounded-[24px] border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
                        <p className="font-bold text-slate-900">Ничего не найдено по текущим фильтрам</p>
                        <button type="button" onClick={() => { setQuery(""); setFilter("all"); }} className="mt-3 text-sm font-semibold text-amber-700 hover:text-amber-900">Сбросить фильтры</button>
                    </div>
                ) : null}

                {visibleItems.length > 0 ? (
                    <>
                        <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-sm text-slate-500">
                            <span>Показано: <b className="text-slate-800">{visibleItems.length}</b> из {items.length}</span>
                            {shortage > 0 && nextPage == null ? <span>HH вернул меньше 10 доступных резюме для этой вакансии.</span> : null}
                        </div>
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {visibleItems.map((item) => (
                                <article key={item.id} className="group flex min-h-[360px] flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-xl hover:shadow-slate-200/70">
                                    <div className="h-1 bg-gradient-to-r from-amber-300 via-amber-500 to-orange-400" />
                                    <div className="flex flex-1 flex-col p-5">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-3">
                                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-sm font-bold text-amber-300">{initials(item.name, item.title)}</div>
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-semibold text-slate-700">{item.name || "Имя скрыто"}{item.age ? ` · ${item.age} лет` : ""}</p>
                                                    <p className="mt-0.5 text-xs text-slate-400">Резюме HH.ru</p>
                                                </div>
                                            </div>
                                            {item.invited ? <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-700">Приглашён</span> : item.candidate_id ? <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-800">Сохранён</span> : <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-bold text-sky-700">Новый</span>}
                                        </div>

                                        <h2 className="mt-5 line-clamp-2 text-lg font-bold leading-6 text-slate-950">{item.title || "Должность не указана"}</h2>
                                        <div className="mt-4 grid grid-cols-2 gap-2">
                                            <div className="rounded-xl bg-slate-50 px-3 py-2.5"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Город</p><p className="mt-1 truncate text-sm font-semibold text-slate-700">{item.area || "Не указан"}</p></div>
                                            <div className="rounded-xl bg-slate-50 px-3 py-2.5"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Опыт</p><p className="mt-1 text-sm font-semibold text-slate-700">{experienceText(item.experience_months)}</p></div>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2.5">
                                            <span className="text-xs font-medium text-slate-400">Ожидания</span><span className="text-sm font-bold text-slate-900">{salaryText(item.salary)}</span>
                                        </div>

                                        <div className="mt-auto grid grid-cols-2 gap-2 pt-5">
                                            <button type="button" aria-label="Открыть резюме" onClick={() => openResume(item)} disabled={resumeModal?.loading && resumeModal?.item?.id === item.id} className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60">{resumeModal?.loading && resumeModal?.item?.id === item.id ? "Загружаем…" : "Открыть резюме"}</button>
                                            {item.candidate_id ? <Link to="/candidates" className="inline-flex items-center justify-center rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100">В кандидатах →</Link> : <button type="button" aria-label="Сохранить в кандидаты" onClick={() => saveCandidate(item)} disabled={Boolean(savingId)} className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm font-semibold text-amber-900 transition hover:bg-amber-100 disabled:opacity-50">{savingId === item.id ? "Сохраняем…" : "Сохранить"}</button>}
                                            <button type="button" aria-label={item.invited ? "Приглашён" : "Пригласить на интервью"} onClick={() => openInvite(item)} disabled={item.invited} className="col-span-2 rounded-xl bg-slate-950 px-3 py-3 text-sm font-bold text-white transition hover:bg-amber-500 hover:text-slate-950 disabled:cursor-default disabled:bg-emerald-100 disabled:text-emerald-800">{item.invited ? "✓ Приглашение отправлено" : "Пригласить на интервью"}</button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                        {nextPage != null ? <div className="flex justify-center pt-2"><button type="button" onClick={() => load(nextPage)} disabled={loading} className="inline-flex min-w-48 items-center justify-center rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:border-amber-400 hover:bg-amber-50 disabled:opacity-50">{loading ? "Загружаем…" : "Показать ещё"}</button></div> : null}
                    </>
                ) : null}
            </div>

            {selected ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onMouseDown={(event) => { if (event.target === event.currentTarget && !sending) setSelected(null); }}>
                    <form onSubmit={submitInvite} role="dialog" aria-modal="true" aria-label="Приглашение кандидата" className="w-full max-w-xl overflow-hidden rounded-[28px] bg-white shadow-2xl">
                        <div className="relative bg-slate-950 px-6 py-6 text-white">
                            <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-amber-400/20 blur-3xl" />
                            <div className="relative flex items-start justify-between gap-4">
                                <div><p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-300">HH.ru</p><h2 className="mt-2 text-2xl font-bold">Пригласить на интервью</h2><p className="mt-1 text-sm text-slate-300">{selected.title}</p></div>
                                <button type="button" onClick={() => setSelected(null)} disabled={sending} aria-label="Закрыть" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xl hover:bg-white/20">×</button>
                            </div>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-bold text-slate-800" htmlFor="cold-invite-message">Сообщение кандидату</label>
                            <textarea id="cold-invite-message" rows="7" required minLength="10" maxLength="4000" value={message} onChange={(event) => setMessage(event.target.value)} disabled={sending} className="mt-2 w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 p-4 text-sm leading-6 outline-none transition focus:border-amber-500 focus:bg-white focus:ring-4 focus:ring-amber-100" />
                            <div className="mt-2 flex items-start justify-between gap-3 text-xs text-slate-500"><p>Сообщение уйдёт через HH.ru после подтверждения.</p><span>{message.length}/4000</span></div>
                            {inviteError ? <p role="alert" className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{inviteError}</p> : null}
                            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <button type="button" onClick={() => setSelected(null)} disabled={sending} className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Отмена</button>
                                <button type="submit" disabled={sending || message.trim().length < 10} className="rounded-xl bg-amber-400 px-5 py-2.5 text-sm font-bold text-slate-950 shadow-sm hover:bg-amber-300 disabled:opacity-50">{sending ? "Отправляем…" : "Отправить приглашение"}</button>
                            </div>
                        </div>
                    </form>
                </div>
            ) : null}

            {resumeModal ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm sm:p-6" onMouseDown={(event) => { if (event.target === event.currentTarget) setResumeModal(null); }}>
                    <div role="dialog" aria-modal="true" aria-label="Резюме кандидата" className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
                        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
                            <div><h2 className="text-lg font-bold text-slate-900">Резюме кандидата</h2><p className="mt-0.5 text-xs text-slate-500">Полная карточка из холодного поиска HH.ru</p></div>
                            <button type="button" onClick={() => setResumeModal(null)} aria-label="Закрыть резюме" className="flex h-9 w-9 items-center justify-center rounded-full text-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">×</button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
                            {resumeModal.loading ? <div role="status" className="flex min-h-64 items-center justify-center text-sm font-medium text-slate-500">Загружаем полное резюме…</div> : null}
                            {resumeModal.error ? <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800">{resumeModal.error}</div> : null}
                            {resumeModal.candidate ? (
                                <CandidateProfileCard
                                    candidate={resumeModal.candidate}
                                    vacancyTitle={vacancy?.name || null}
                                    showAi={false}
                                    aiHint="AI-оценка появится после сохранения кандидата."
                                    actions={resumeModal.candidate.hh_resume_link ? <a href={resumeModal.candidate.hh_resume_link} target="_blank" rel="noreferrer" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Открыть на HH.ru ↗</a> : null}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
        </MainLayout>
    );
}
